import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ImageUrlPipe, InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { finalize } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { PageTitleService } from '../../navigation/title/title.service';
import { WindowService } from '../../utilities';
import { ShowPlayerComponent } from '../show-player/show-player.component';
import { ShowsLikeButtonComponent } from '../shows-like-button/shows-like-button.component';
import { playerLink } from '../shows-player';
import { ShowsTranslationFollower } from '../shows-translation';
import { ChannelLink, Clip, DEFAULT_SHOWS_DEFINITION, ShowsDefinition, ShowsItemRef, Tool, clipPath } from '../shows.model';
import { ShowsService } from '../shows.service';
import { toolTypeKey } from '../tool-type';

/**
 * One tool: `/tools/{tool}` — what it is, where to find it, who recommends it, and the episodes
 * it appeared in.
 */
@Component({
  selector: 'multisite-tool-page',
  imports: [DatePipe, RouterModule, TranslocoModule, ImageUrlPipe, InsightMarkdownPipe, LoaderGlobeAnimationDetailComponent, ShowPlayerComponent, ShowsLikeButtonComponent],
  providers: [provideTranslocoScope('tools')],
  template: `
    <div class="container my-4" *transloco="let t">
      @let current = tool();

      @if (current) {
        <div class="row">
          <div class="col-lg-8">
            <small class="text-muted d-block">{{ t(toolTypeKey(current.type)) }}</small>
            <h1 class="font-weight-bold text-break">{{ current.name }}</h1>

            @if (translation.translating()) {
              <div class="d-flex align-items-center my-2" role="status">
                <multisite-loader-globe-animation-detail [width]="40" [height]="40"></multisite-loader-globe-animation-detail>
                <span class="ml-2">{{ t('news.translating') }}</span>
              </div>
            }

            @if (current.images[0]; as image) {
              <img class="w-100 my-3" [src]="image | imageUrl: 'w_700,c_fill,ar_16:9'" [alt]="image.alt_text ?? ''" />
            }

            @if (current.summary) {
              <div class="lead text-break" [innerHTML]="current.summary | insightMarkdown"></div>
            }
            @if (current.description) {
              <div class="my-3 text-break" [innerHTML]="current.description | insightMarkdown"></div>
            }

            @if (current.url) {
              <a class="btn btn-primary my-2" [href]="current.url" target="_blank" rel="noopener">{{ t('tools.open_tool') }}</a>
            }

            @for (clip of demonstrations(); track clip.id) {
              <div class="my-4">
                <multisite-show-player [link]="clip.player" [title]="clip.title"></multisite-show-player>
                <a class="small" [routerLink]="clipPath(definition, clip)">{{ clip.title }}</a>
              </div>
            }
          </div>

          <div class="col-lg-4">
            <div class="sticky-top pt-3">
              <div class="mb-3"><multisite-shows-like-button [item]="ref()"></multisite-shows-like-button></div>

              @if (current.recommendedBy.length) {
                <p class="mb-4">{{ t('tools.recommenders', { names: current.recommendedBy.join(', ') }) }}</p>
              }

              @if (current.episodes.length) {
                <p class="h5">{{ t('tools.in_episodes') }}</p>
                <div class="list-group list-group-flush mb-4">
                  @for (episode of current.episodes; track episode.id) {
                    <a class="list-group-item list-group-item-action px-0" [routerLink]="[definition.showsPath, episode.show, episode.slug]">
                      <span class="d-block">{{ episode.title }}</span>
                      <small class="text-muted">{{ episode.publishedAt | date: 'mediumDate' }}</small>
                    </a>
                  }
                </div>
              }

              <a class="btn btn-link px-0" [routerLink]="[definition.toolsPath]">{{ t('tools.all_tools') }} <i class="fa-solid fa-angle-right ml-1"></i></a>
            </div>
          </div>
        </div>
      }

      @if (!current && loading()) {
        <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
      }

      @if (notFound()) {
        <div class="text-center my-5">
          <p>{{ t('common.not_found') }}</p>
          <a [routerLink]="[definition.toolsPath]">{{ t('tools.all_tools') }}</a>
        </div>
      }

      @if (failed()) {
        <p class="text-danger my-5 text-center">{{ t('error.could_not_load') }}</p>
      }
    </div>
  `,
})
export class ToolPageComponent {
  private route = inject(ActivatedRoute);
  private showsService = inject(ShowsService);
  private pageTitleService = inject(PageTitleService);
  private windowService = inject(WindowService);

  readonly definition: ShowsDefinition = this.route.snapshot.data['showsSection'] ?? DEFAULT_SHOWS_DEFINITION;
  readonly toolTypeKey = toolTypeKey;
  readonly translation = new ShowsTranslationFollower<Tool>();

  tool = signal<Tool | null>(null);
  loading = signal(false);
  notFound = signal(false);
  failed = signal(false);

  readonly slug = signal('');
  readonly ref = computed<ShowsItemRef>(() => ({ kind: 'tool', slug: this.slug() }));
  readonly clipPath = clipPath;

  /** The tool's demonstrations that can be played on this page, each with its player. */
  readonly demonstrations = computed(() =>
    (this.tool()?.clips ?? [])
      .map((clip) => ({ ...clip, player: playerLink(clip.channelLinks) }))
      .filter((clip): clip is Clip & { player: ChannelLink } => clip.player !== null),
  );

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.slug.set(params.get('tool') ?? '');
      this.windowService.goToTop();
      this.load();
    });
    reloadOnLocaleChange(() => this.load());
  }

  private load(): void {
    this.translation.stop();
    this.loading.set(true);
    this.notFound.set(false);
    this.failed.set(false);

    this.showsService
      .getTool(this.slug())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (tool) => {
          this.present(tool);
          this.translation.follow(this.ref(), tool, (translated) => this.present({ ...translated, episodes: tool.episodes, clips: tool.clips, images: tool.images }));
        },
        error: (error) => (error?.status === 404 ? this.notFound.set(true) : this.failed.set(true)),
      });
  }

  private present(tool: Tool): void {
    this.tool.set(tool);
    this.pageTitleService.setTitle(tool.name);
  }
}
